import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

interface Shop {
  id: number;
  name: string;
  api_key: string;
  currency: string;
  payment_amount_divisor?: number;
}

const Shops: React.FC = () => {
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [shopToDelete, setShopToDelete] = useState<Shop | null>(null);
  const [showShopInfo, setShowShopInfo] = useState<number | null>(null);
  const [shopInfo, setShopInfo] = useState<any>(null);
  const [formData, setFormData] = useState({ name: '', apiKey: '', currency: 'RUB' });
  const [editData, setEditData] = useState({ name: '', currency: 'RUB', paymentAmountDivisor: '1' });
  const [showCloneForm, setShowCloneForm] = useState<Shop | null>(null);
  const [cloneData, setCloneData] = useState({ name: '', apiKey: '', currency: 'RUB' });
  const [error, setError] = useState('');

  const availableCurrencies = [
    { code: 'RUB', name: 'Российский рубль (₽)' },
    { code: 'KZT', name: 'Казахстанский тенге (₸)' },
    { code: 'USD', name: 'Доллар США ($)' },
    { code: 'EUR', name: 'Евро (€)' }
  ];

  useEffect(() => {
    loadShops();
  }, []);

  const loadShops = async () => {
    try {
      const response = await api.get('/owner/shops');
      setShops(response.data);
    } catch (error) {
      console.error('Failed to load shops:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await api.post('/owner/shops', formData);
      setFormData({ name: '', apiKey: '', currency: 'RUB' });
      setShowCreateForm(false);
      loadShops();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка создания магазина');
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showEditForm) return;

    try {
      await api.put(`/owner/shops/${showEditForm}`, {
        ...editData,
        paymentAmountDivisor: Number(editData.paymentAmountDivisor) || 1,
      });
      setShowEditForm(null);
      setEditData({ name: '', currency: 'RUB', paymentAmountDivisor: '1' });
      loadShops();
      alert('Магазин успешно обновлен!');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка обновления магазина');
    }
  };

  const handleRotateApiKey = async (shopId: number) => {
    if (!confirm('Вы уверены, что хотите изменить API ключ? Это может нарушить работу интеграций.')) {
      return;
    }

    try {
      const response = await api.post(`/owner/shops/${shopId}/rotate-api-key`, {});
      
      if (response.data.success) {
        alert(`Новый API ключ: ${response.data.newApiKey}`);
        loadShops();
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Ошибка при изменении API ключа');
    }
  };

  const handleDeleteShop = async (shopId: number) => {
    try {
      await api.delete(`/owner/shops/${shopId}`);
      
      setShowDeleteConfirm(null);
      setShopToDelete(null);
      loadShops();
      alert('Магазин успешно удален!');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Ошибка при удалении магазина';
      const details = err.response?.data?.details || '';
      alert(`${errorMessage}${details ? `: ${details}` : ''}`);
    }
  };

  const confirmDelete = (shop: Shop) => {
    setShopToDelete(shop);
    setShowDeleteConfirm(shop.id);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
    setShopToDelete(null);
  };

  const loadShopInfo = async (shopId: number) => {
    try {
      const response = await api.get(`/owner/shops/${shopId}`);
      setShopInfo(response.data);
      // Предзаполняем форму редактирования
    const divisor = response.data?.settings?.paymentAmountDivisor ?? response.data?.settings?.payment_amount_divisor ?? response.data?.shop?.payment_amount_divisor ?? 1;
    setEditData({ name: response.data.shop.name, currency: response.data.shop.currency, paymentAmountDivisor: String(divisor) });
      setCloneData({
        name: `${response.data.shop.name} (копия)`,
        apiKey: '',
        currency: response.data.shop.currency || 'RUB'
      });
      setShowShopInfo(shopId);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Ошибка при загрузке информации о магазине');
    }
  };

  const closeShopInfo = () => {
    setShowShopInfo(null);
    setShopInfo(null);
  };

  const startEdit = (shop: Shop) => {
    const divisor = (shop as any)?.payment_amount_divisor ?? 1;
    setEditData({ name: shop.name, currency: shop.currency, paymentAmountDivisor: String(divisor) });
    setCloneData({ name: `${shop.name} (копия)`, apiKey: '', currency: shop.currency });
    setShowEditForm(shop.id);
  };

  const cancelEdit = () => {
    setShowEditForm(null);
    setEditData({ name: '', currency: 'RUB', paymentAmountDivisor: '1' });
  };

  const handleClone = async (e: React.FormEvent, sourceShopId: number) => {
    e.preventDefault();
    try {
      const payload: any = {
        name: cloneData.name.trim(),
        currency: cloneData.currency
      };
      if (cloneData.apiKey.trim()) {
        payload.apiKey = cloneData.apiKey.trim();
      }
      const resp = await api.post(`/owner/shops/${sourceShopId}/clone`, payload);
      alert(`Магазин клонирован. ID: ${resp.data?.shop?.id}, API ключ: ${resp.data?.shop?.apiKey}`);
      setShowCloneForm(null);
      loadShops();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Ошибка клонирования магазина');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Управление магазинами</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Создать магазин
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Форма создания магазина */}
      {showCreateForm && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Создать новый магазин</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Название магазина</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">API ключ</label>
              <input
                type="text"
                value={formData.apiKey}
                onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Валюта</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
              >
                {availableCurrencies.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Создать
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Список магазинов */}
      {loading ? (
        <div className="text-center py-8">Загрузка...</div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {shops.map((shop) => (
              <li key={shop.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    {showEditForm === shop.id ? (
                      <form onSubmit={handleEdit} className="flex items-center space-x-4">
                        <input
                          type="text"
                          value={editData.name}
                          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                          className="border border-gray-300 rounded-md px-3 py-2 flex-1"
                          required
                        />
                        <select
                          value={editData.currency}
                          onChange={(e) => setEditData({ ...editData, currency: e.target.value })}
                          className="border border-gray-300 rounded-md px-3 py-2"
                        >
                          {availableCurrencies.map((currency) => (
                            <option key={currency.code} value={currency.code}>
                              {currency.name}
                            </option>
                          ))}
                        </select>
                        <button
                          type="submit"
                          className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700"
                        >
                          Сохранить
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          className="bg-gray-300 text-gray-700 px-3 py-2 rounded-md hover:bg-gray-400"
                        >
                          Отмена
                        </button>
                      </form>
                    ) : (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{shop.name}</h3>
                        <p className="text-sm text-gray-500">API ключ: {shop.api_key}</p>
                        <p className="text-sm text-gray-500">Валюта: {shop.currency}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    {showEditForm !== shop.id && (
                      <button
                        onClick={() => loadShopInfo(shop.id)}
                        className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700"
                      >
                        Информация
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Модальное окно с информацией о магазине */}
      {showShopInfo && shopInfo && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Информация о магазине</h3>
                <button
                  onClick={closeShopInfo}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Основная информация */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Основная информация</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-500">ID:</span>
                      <span className="ml-2 text-sm text-gray-900">{shopInfo.shop.id}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Название:</span>
                      <span className="ml-2 text-sm text-gray-900">{shopInfo.shop.name}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">API ключ:</span>
                      <span className="ml-2 text-sm text-gray-900 font-mono">{shopInfo.shop.api_key}</span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Валюта:</span>
                      <span className="ml-2 text-sm text-gray-900">{shopInfo.shop.currency}</span>
                    </div>
                  </div>
                </div>

                {/* Статистика таблиц */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Статистика данных</h4>
                  <div className="space-y-2">
                    {Object.entries(shopInfo.tableStats).map(([table, count]) => (
                      <div key={table}>
                        <span className="text-sm font-medium text-gray-500">
                          {table === 'payment_requisites' ? 'Реквизиты:' :
                           table === 'buyers' ? 'Покупатели:' :
                           table === 'payment_requests' ? 'Платежи:' :
                           table === 'provider_transactions' ? 'Транзакции:' :
                           table === 'matches' ? 'Сопоставления:' :
                           table === 'audit_logs' ? 'Аудит:' :
                           table === 'operators' ? 'Операторы:' :
                           table === 'shop_settings' ? 'Настройки:' :
                           table === 'global_users' ? 'Пользователи системы:' :
                           table === 'global_api_keys' ? 'API ключи системы:' : table}:
                        </span>
                        <span className="ml-2 text-sm text-gray-900">{String(count)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Настройки магазина (Owner) */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Настройки магазина</h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-700">Передавать external_trx в вебхуках</div>
                        <div className="text-xs text-gray-500">Вернуть магазину их trx в поле external_trx</div>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const current = Boolean(shopInfo?.settings?.includeTransactionIdInCallbacks);
                            const next = !current;
                            setShopInfo((s: any) => ({ ...s, settings: { ...(s?.settings || {}), includeTransactionIdInCallbacks: next } }));
                            await api.put(`/owner/shops/${shopInfo.shop.id}`, { includeTransactionIdInCallbacks: next });
                          } catch (e: any) {
                            alert(e.response?.data?.error || 'Не удалось обновить настройку');
                          }
                        }}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${shopInfo?.settings?.includeTransactionIdInCallbacks ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-800'}`}
                      >
                        {shopInfo?.settings?.includeTransactionIdInCallbacks ? 'Включено' : 'Выключено'}
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-700">Передавать buyer_paid в вебхуках</div>
                        <div className="text-xs text-gray-500">Отправлять сумму, выставленную покупателю (reserved_amount), в поле buyer_paid</div>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const current = Boolean(shopInfo?.settings?.includeBuyerPaidInCallbacks || shopInfo?.settings?.include_buyer_paid_in_callbacks === '1');
                            const next = !current;
                            setShopInfo((s: any) => ({ ...s, settings: { ...(s?.settings || {}), includeBuyerPaidInCallbacks: next, include_buyer_paid_in_callbacks: next ? '1' : '0' } }));
                            await api.put(`/owner/shops/${shopInfo.shop.id}`, { includeBuyerPaidInCallbacks: next });
                          } catch (e: any) {
                            alert(e.response?.data?.error || 'Не удалось обновить настройку');
                          }
                        }}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${ (shopInfo?.settings?.includeBuyerPaidInCallbacks || shopInfo?.settings?.include_buyer_paid_in_callbacks === '1') ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-800'}`}
                      >
                        {(shopInfo?.settings?.includeBuyerPaidInCallbacks || shopInfo?.settings?.include_buyer_paid_in_callbacks === '1') ? 'Включено' : 'Выключено'}
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-700">Отправлять ID транзакции покупателя в вебхуках</div>
                        <div className="text-xs text-gray-500">Отправлять в ответ магазину ID (номер транзакции покупателя) в поле id</div>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const current = Boolean(shopInfo?.settings?.includePaymentIdInCallbacks);
                            const next = !current;
                            setShopInfo((s: any) => ({ ...s, settings: { ...(s?.settings || {}), includePaymentIdInCallbacks: next } }));
                            await api.put(`/owner/shops/${shopInfo.shop.id}`, { includePaymentIdInCallbacks: next });
                          } catch (e: any) {
                            alert(e.response?.data?.error || 'Не удалось обновить настройку');
                          }
                        }}
                        className={`px-3 py-1 rounded-md text-sm font-medium ${shopInfo?.settings?.includePaymentIdInCallbacks ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-800'}`}
                      >
                        {shopInfo?.settings?.includePaymentIdInCallbacks ? 'Включено' : 'Выключено'}
                      </button>
                    </div>

                    {/* Telegram настройки перенесены в раздел "Уведомления" панели владельца */}
                  </div>
                </div>

                {/* Действия */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Действия</h4>
                  <div className="space-y-4">
                    {/* Редактировать имя и валюту */}
                    <form
                      onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                          await api.put(`/owner/shops/${shopInfo.shop.id}`, {
                            ...editData,
                            paymentAmountDivisor: Number(editData.paymentAmountDivisor) || 1,
                          });
                          const refreshed = await api.get(`/owner/shops/${shopInfo.shop.id}`);
                          setShopInfo(refreshed.data);
                          alert('Магазин успешно обновлен');
                        } catch (err: any) {
                          alert(err.response?.data?.error || 'Ошибка обновления магазина');
                        }
                      }}
                      className="grid grid-cols-1 md:grid-cols-3 gap-3"
                    >
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Название</label>
                        <input
                          type="text"
                          value={editData.name}
                          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Валюта</label>
                        <select
                          value={editData.currency}
                          onChange={(e) => setEditData({ ...editData, currency: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                          {availableCurrencies.map((c) => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Делитель суммы (payment_amount_divisor)</label>
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={editData.paymentAmountDivisor}
                          onChange={(e) => setEditData({ ...editData, paymentAmountDivisor: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                          required
                        />
                      </div>
                      <div className="flex items-end">
                        <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 w-full">Сохранить</button>
                      </div>
                    </form>

                    {/* Сменить API ключ */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-700">Сменить API ключ</div>
                        <div className="text-xs text-gray-500">Будет сгенерирован новый API ключ, старый станет недействителен</div>
                      </div>
                      <button
                        onClick={() => handleRotateApiKey(shopInfo.shop.id)}
                        className="bg-yellow-600 text-white px-3 py-2 rounded-md hover:bg-yellow-700"
                      >
                        Сменить API ключ
                      </button>
                    </div>

                    {/* Клонировать магазин */}
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="text-sm font-medium text-gray-700">Клонировать магазин</div>
                          <div className="text-xs text-gray-500">Копируются настройки, ссылки, дизайн и реквизиты. Имя и API ключ должны быть уникальны. Транзакции и пользователи не копируются.</div>
                        </div>
                        <button
                          onClick={() => setShowCloneForm(shopInfo.shop)}
                          className="bg-purple-600 text-white px-3 py-2 rounded-md hover:bg-purple-700"
                        >
                          Клонировать
                        </button>
                      </div>

                      {showCloneForm && showCloneForm.id === shopInfo.shop.id && (
                        <form
                          onSubmit={(e) => handleClone(e, shopInfo.shop.id)}
                          className="grid grid-cols-1 md:grid-cols-3 gap-3"
                        >
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Новое название</label>
                            <input
                              type="text"
                              value={cloneData.name}
                              onChange={(e) => setCloneData({ ...cloneData, name: e.target.value })}
                              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Новый API ключ (опционально)</label>
                            <input
                              type="text"
                              value={cloneData.apiKey}
                              onChange={(e) => setCloneData({ ...cloneData, apiKey: e.target.value })}
                              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                              placeholder="Оставьте пустым, чтобы сгенерировать"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Валюта</label>
                            <select
                              value={cloneData.currency}
                              onChange={(e) => setCloneData({ ...cloneData, currency: e.target.value })}
                              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                            >
                              {availableCurrencies.map((c) => (
                                <option key={c.code} value={c.code}>{c.name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="md:col-span-3 flex gap-2">
                            <button type="submit" className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700">
                              Создать клон
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowCloneForm(null)}
                              className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300"
                            >
                              Отмена
                            </button>
                          </div>
                        </form>
                      )}
                    </div>

                    {/* Удаление перенесено ниже (раздел Возможность удаления) */}
                  </div>
                </div>
              </div>

              {/* Статистика платежей */}
              {shopInfo.tableStats.paymentStatuses && (
                <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Статистика платежей по статусам</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(shopInfo.tableStats.paymentStatuses).map(([status, count]) => (
                      <div key={status} className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{String(count)}</div>
                        <div className="text-sm text-gray-500">
                          {status === 'pending' ? 'В ожидании' :
                           status === 'processing' ? 'В обработке' :
                           status === 'completed' ? 'Завершено' :
                           status === 'cancelled' ? 'Отменено' :
                           status === 'failed' ? 'Ошибка' : status}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Возможность удаления */}
              <div className="mt-6 bg-red-50 p-4 rounded-lg border border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-red-900">Возможность удаления</h4>
                    <p className="text-sm text-red-700 mt-1">
                      {shopInfo.canDelete 
                        ? 'Магазин можно безопасно удалить' 
                        : 'Магазин нельзя удалить из-за активных платежей'}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      closeShopInfo();
                      confirmDelete(shopInfo.shop);
                    }}
                    disabled={!shopInfo.canDelete}
                    className={`px-4 py-2 rounded-md font-medium ${
                      shopInfo.canDelete
                        ? 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500'
                        : 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    }`}
                  >
                    Удалить магазин
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления */}
      {showDeleteConfirm && shopToDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Подтверждение удаления</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Вы действительно хотите удалить магазин <strong>"{shopToDelete.name}"</strong>?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Это действие нельзя отменить. Будут удалены все данные магазина:
                </p>
                <ul className="text-sm text-gray-500 mt-2 text-left list-disc list-inside">
                  <li>Настройки магазина</li>
                  <li>Платежные реквизиты</li>
                  <li>История платежей</li>
                  <li>Данные покупателей</li>
                  <li>Аудит и логи</li>
                </ul>
              </div>
              <div className="items-center px-4 py-3">
                <button
                  onClick={() => handleDeleteShop(shopToDelete.id)}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Удалить магазин
                </button>
                <button
                  onClick={cancelDelete}
                  className="mt-3 px-4 py-2 bg-gray-300 text-gray-700 text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shops;

